import { useEffect, useState } from 'react';

// Factory for the module-level "load once per session" lookup-cache pattern
// (see useAssetTypes for the canonical hand-written version). Returns a hook +
// a cache-clearer to wire into AuthContext.logout.
export interface LookupState<T> {
  data: T[] | null;
  loading: boolean;
  error: string | null;
}

export function createLookupHook<T>(loader: () => Promise<T[]>): {
  useHook: () => LookupState<T>;
  clear: () => void;
} {
  let cache: T[] | null = null;
  let inflight: Promise<T[]> | null = null;

  function load(): Promise<T[]> {
    if (cache) return Promise.resolve(cache);
    if (inflight) return inflight;
    inflight = loader()
      .then((d) => {
        cache = d;
        inflight = null;
        return d;
      })
      .catch((e: unknown) => {
        inflight = null;
        throw e;
      });
    return inflight;
  }

  function clear(): void {
    cache = null;
    inflight = null;
  }

  function useHook(): LookupState<T> {
    const [state, setState] = useState<LookupState<T>>(() =>
      cache
        ? { data: cache, loading: false, error: null }
        : { data: null, loading: true, error: null },
    );
    useEffect(() => {
      if (cache) {
        setState((prev) =>
          prev.data === cache ? prev : { data: cache, loading: false, error: null },
        );
        return;
      }
      let cancelled = false;
      load()
        .then((d) => {
          if (!cancelled) setState({ data: d, loading: false, error: null });
        })
        .catch((e: unknown) => {
          if (!cancelled)
            setState({
              data: null,
              loading: false,
              error: e instanceof Error ? e.message : String(e),
            });
        });
      return () => {
        cancelled = true;
      };
    }, []);
    return state;
  }

  return { useHook, clear };
}
